import { useCallback, useEffect, useRef, useState } from 'react'
import maleAvatar from "../../assets/maleAvatar.webp"
import logo from "../../assets/logo.png"
import { NavbarStyle } from '../styled/navbarStyled/navbar'
import { Link } from 'react-router-dom'
import styles from "./Navbar.module.css"
import NavbarModule from './navbarModule/NavbarModule'
import Numerator from '../../shopping/numerator/Numerator'
import { auth } from '../../firebase/firebase'
import { useTeacher } from '../../context/TeacherProvider'
import { useSelector } from "react-redux"
import LanguageSwitcher from '../../translation/LanguageSwitcher'
import { useTranslation } from 'react-i18next'

const Navbar = () => {
  const { t } = useTranslation()
  const navbarModuleRef = useRef(null)
  const avatarImageRef = useRef(null)
  const { role } = useSelector((state) => state.login)
  const isStudent = role === "student"
  const [localImage, setLocalImage] = useState(maleAvatar)
  const [navbarModuleVisibility, setNavbarModuleVisibility] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const { setTeacherID } = useTeacher()
  const teacherData = useSelector((state) => state.teacher.data)
  const studentData = useSelector((state) => state.student.data)
  const teacherImage = useSelector((state) => state.teacher.profileImage)
  const studentImage = useSelector((state) => state.student.profileImage)

  const profilePath = isStudent ? "/student-profile" : "/profile"
  const profileImage = isStudent ? studentImage : teacherImage
  const displayName = isStudent
    ? `${studentData?.firstName || ""} ${studentData?.lastName || ""}`.trim() || auth.currentUser?.displayName
    : `Tutor ${teacherData?.firstName || auth.currentUser?.displayName}`
  const avatarAlt = isStudent
    ? `${studentData?.firstName} ${studentData?.lastName}` || "User"
    : teacherData?.firstName || "User"

  const navLinks = isStudent
    ? [
        { to: "/student-profile", label: t("Navbar.profile") },
        { to: "/student-classes", label: t("Navbar.myLessons") },
        {
          to: "/calendar",
          label: t("Navbar.bookALesson"),
          onNavigate: () => setTeacherID(null),
        },
      ]
    : [
        { to: "/profile", label: t("Navbar.profile") },
        { to: "/classes", label: t("Navbar.classes") },
        { to: "/slots", label: t("Navbar.slots") },
      ]

  const closeMenu = useCallback(() => setMenuOpen(false), [])

  const toggleMenu = () => {
    setNavbarModuleVisibility(false)
    setMenuOpen((open) => !open)
  }

  const toggleProfileMenu = () => {
    setMenuOpen(false)
    setNavbarModuleVisibility((visible) => !visible)
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        navbarModuleRef.current &&
        !navbarModuleRef.current.contains(event.target) &&
        avatarImageRef.current &&
        !avatarImageRef.current.contains(event.target)
      ) {
        setNavbarModuleVisibility(false)
      }
    }

    if (navbarModuleVisibility) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [navbarModuleVisibility])

  useEffect(() => {
    if (!menuOpen) return undefined

    document.body.style.overflow = "hidden"

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false)
      }
    }

    window.addEventListener("keydown", onKeyDown)

    return () => {
      document.body.style.overflow = ""
      window.removeEventListener("keydown", onKeyDown)
    }
  }, [menuOpen])

  return (
    <NavbarStyle className={styles.navbar}>
      <Link
        to="/home"
        className={styles.navLogoWrap}
        aria-label={t("Navbar.home")}
        onClick={closeMenu}
      >
        {!isStudent && <img src={logo} alt="" className={styles.navLogo} />}
      </Link>

      <button
        type="button"
        className={`${styles.menuToggle} ${menuOpen ? styles.menuToggleOpen : ""}`}
        aria-expanded={menuOpen}
        aria-controls="app-nav"
        aria-label={menuOpen ? t("Navbar.closeMenu") : t("Navbar.openMenu")}
        onClick={toggleMenu}
      >
        <span className={styles.menuToggleBar} />
        <span className={styles.menuToggleBar} />
        <span className={styles.menuToggleBar} />
      </button>

      <nav
        id="app-nav"
        className={`${styles.headerNav} ${menuOpen ? styles.headerNavOpen : ""}`}
        aria-label={t("Navbar.mainNav")}
      >
        <ul className={styles.navLinks}>
          {navLinks.map(({ to, label, onNavigate }) => (
            <li key={to}>
              <Link
                className={styles.navPillLink}
                to={to}
                onClick={() => {
                  onNavigate?.()
                  closeMenu()
                }}
              >
                {label}
              </Link>
            </li>
          ))}
        </ul>

        <div className={styles.navExtras}>
          {isStudent && (
            <LanguageSwitcher
              variant="light"
              fullWidth
              className={styles.navLanguageWrapper}
              selectClassName={styles.navLanguageSelect}
            />
          )}
          <Link to="/home" className={styles.navHomeBtn} onClick={closeMenu}>
            {t("Navbar.home")}
          </Link>
          <Link
            to={profilePath}
            className={`${styles.navUserName} ${styles.navPillLink}`}
            onClick={closeMenu}
          >
            {displayName}
          </Link>
        </div>
      </nav>

      <div className={styles.navbarEnd}>
        <div className={styles.imageDiv}>
          <img
            ref={avatarImageRef}
            className={styles.avatarImage}
            onClick={toggleProfileMenu}
            src={profileImage || maleAvatar}
            alt={avatarAlt}
            onError={(e) => {
              e.target.src = maleAvatar
              setLocalImage(maleAvatar)
            }}
          />
          {!isStudent && <Numerator />}
        </div>
      </div>

      {menuOpen && (
        <button
          type="button"
          className={styles.menuBackdrop}
          aria-label={t("Navbar.closeMenu")}
          onClick={closeMenu}
        />
      )}

      {navbarModuleVisibility && (
        <NavbarModule
          ref={navbarModuleRef}
          img={localImage}
          className={styles.navbarModule}
        />
      )}
    </NavbarStyle>
  )
}

export default Navbar
